import { useCallback, useMemo, useState } from 'react';
import { Table } from 'antd';
import type { CSSProperties } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { COLOR } from '@styles/theme';
import DeleteReasonPopup from '../DeleteLotPopup';
import { InventoryLotSubTable } from './InventoryLotSubTable';
import {
  InventoryLotsColumnTitle,
  InventoryLotsExpandIcon,
} from './InventoryLotsExpandColumn';
import { EXPAND_COLUMN_WIDTH, InventoryTableWrapper } from './inventoryListStyled';
import {
  renderInventoryProductCell,
  type InventoryListCellContext,
} from './inventoryListCells';
import {
  getProductRowKey,
  itemHasLots,
  type InventoryListApiItem,
  type InventoryListColumnKey,
  type LocationSummary,
} from './inventoryListTypes';

const NO_COLUMN_DIVIDER: CSSProperties = {
  borderInlineEnd: 'none',
};

const CELL_STYLE: CSSProperties = {
  verticalAlign: 'middle',
};

const COLUMN_DEFS: { key: InventoryListColumnKey; title: string; width: string }[] = [
  { key: 'description', title: 'Product Description', width: '22%' },
  { key: 'location', title: 'Location', width: '14%' },
  { key: 'carton_configurations', title: 'Carton Configurations', width: '20%' },
  { key: 'on_hand', title: 'On Hand', width: '8%' },
  { key: 'transfer', title: 'Transfer', width: '8%' },
  { key: 'allocated', title: 'Allocated', width: '8%' },
  { key: 'total', title: 'Total', width: '8%' },
  { key: 'actions', title: '', width: '12%' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500];

export type InventoryListTableProps = {
  data: InventoryListApiItem[];
  loading?: boolean;
  imperial: boolean;
  filter: { page: number; limit: number };
  totalData: number;
  pageChanged: (page: number) => void;
  filterChanged: (key: string, value: number) => void;
  refresh: () => void;
  openPopup: (
    item: InventoryListApiItem & { lotIndex: number; transferLot?: boolean },
  ) => void;
  editItem: (item: InventoryListApiItem) => void;
  editBarcodes: (item: InventoryListApiItem) => void;
  editRestock: (item: InventoryListApiItem) => void;
  openItemPopupLogs: (item: InventoryListApiItem) => void;
  editLotAttribute: (lot: LocationSummary & { itemId?: string }) => void;
  editPallet: (payload: { lot: LocationSummary } & InventoryListApiItem) => void;
};

export function InventoryListTable({
  data,
  loading,
  imperial,
  filter,
  totalData,
  pageChanged,
  filterChanged,
  refresh,
  openPopup,
  editItem,
  editBarcodes,
  editRestock,
  openItemPopupLogs,
  editLotAttribute,
  editPallet,
}: InventoryListTableProps) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [deletePopupState, setDeletePopupState] = useState<{
    open: boolean;
    item: LocationSummary | null;
    reason: string;
  }>({
    open: false,
    item: null,
    reason: '',
  });

  const cellCtx: InventoryListCellContext = useMemo(
    () => ({
      imperial,
      openPopup,
      editItem,
      editBarcodes,
      editRestock,
      openItemPopupLogs,
      editLotAttribute,
      editPallet,
      onDeleteLot: lot => setDeletePopupState({ open: true, item: lot, reason: '' }),
    }),
    [
      imperial,
      openPopup,
      editItem,
      editBarcodes,
      editRestock,
      openItemPopupLogs,
      editLotAttribute,
      editPallet,
    ],
  );

  const columns: ColumnsType<InventoryListApiItem> = useMemo(() => {
    const dataColumns = COLUMN_DEFS.map(col => ({
      key: col.key,
      title: col.title,
      width: col.width,
      align: ['on_hand', 'transfer', 'allocated', 'total'].includes(col.key)
        ? ('right' as const)
        : undefined,
      onHeaderCell: () => ({ style: { ...NO_COLUMN_DIVIDER, verticalAlign: 'middle' } }),
      onCell: () => ({ style: CELL_STYLE }),
      render: (_: unknown, record: InventoryListApiItem) =>
        renderInventoryProductCell(col.key, record, cellCtx),
    }));

    return [dataColumns[0], Table.EXPAND_COLUMN, ...dataColumns.slice(1)];
  }, [cellCtx]);

  const expandableRowKeys = useMemo(
    () => data.filter(itemHasLots).map(getProductRowKey),
    [data],
  );

  const allLotsExpanded =
    expandableRowKeys.length > 0 &&
    expandableRowKeys.every(key => expandedRowKeys.includes(key));

  const toggleAllLots = useCallback(() => {
    setExpandedRowKeys(allLotsExpanded ? [] : expandableRowKeys);
  }, [allLotsExpanded, expandableRowKeys]);

  const pagination: TablePaginationConfig = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showTotal: total => `${total} records`,
    size: 'small',
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <InventoryTableWrapper>
      <Table<InventoryListApiItem>
        className="inventory-list-table"
        rowKey={getProductRowKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        tableLayout="fixed"
        pagination={pagination}
        showHeader
        expandable={{
          columnTitle: (
            <InventoryLotsColumnTitle
              allExpanded={allLotsExpanded}
              hasExpandableRows={expandableRowKeys.length > 0}
              onToggleAll={toggleAllLots}
            />
          ),
          columnWidth: EXPAND_COLUMN_WIDTH,
          expandedRowKeys,
          onExpandedRowsChange: keys => setExpandedRowKeys(keys as string[]),
          rowExpandable: record => itemHasLots(record),
          expandIcon: ({ expanded, onExpand, record }) => (
            <InventoryLotsExpandIcon
              expanded={expanded}
              record={record}
              onExpand={onExpand}
            />
          ),
          expandedRowRender: record => (
            <InventoryLotSubTable item={record} cellCtx={cellCtx} />
          ),
          expandedRowClassName: () => 'inventory-list-expanded-row',
        }}
        style={{
          background: COLOR.white,
          borderRadius: 6,
          border: `1px solid ${COLOR.border}`,
        }}
      />
      {deletePopupState.open && (
        <DeleteReasonPopup
          open={deletePopupState.open}
          onClose={() => setDeletePopupState(prev => ({ ...prev, open: false }))}
          onConfirm={() => {
            setDeletePopupState({ open: false, item: null, reason: '' });
            refresh();
          }}
          reason={deletePopupState.reason}
          inventoryId={deletePopupState.item?.inventoryId}
          setReason={reason => setDeletePopupState(prev => ({ ...prev, reason }))}
        />
      )}
    </InventoryTableWrapper>
  );
}
